import React from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { el } from "date-fns/locale";
import {
  Area,
  AreaChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock3,
  Eye,
  MessageSquare,
  RefreshCw,
  Send,
  Smartphone,
  Users,
} from "lucide-react";
import { AdminReportNav } from "@/components/admin/AdminReportNav";
import { AdminEmptyState, AdminErrorState, AdminMetricSkeletons } from "@/components/admin/AdminDataState";
import { PageHeader } from "@/components/layouts/PageHeader";
import { StatCard } from "@/components/ui/StatCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useSmsStatistics } from "@/hooks/useAdminReports";
import { cn } from "@/lib/utils";

type Days = 7 | 14 | 30;

const statusMeta = {
  delivered: { label: "Παραδόθηκαν", color: "#059669", text: "text-emerald-700", bg: "bg-emerald-500" },
  sent: { label: "Αποδεκτά από πάροχο", color: "#0369a1", text: "text-sky-700", bg: "bg-sky-700" },
  pending: { label: "Σε αναμονή", color: "#d97706", text: "text-amber-700", bg: "bg-amber-600" },
  failed: { label: "Αποτυχία", color: "#e11d48", text: "text-rose-700", bg: "bg-rose-600" },
};

const formatNumber = (value: number) => new Intl.NumberFormat("el-GR").format(value);

const MessageStatistics = () => {
  const [days, setDays] = React.useState<Days>(14);
  const query = useSmsStatistics(days);
  const stats = query.data;

  const daily = React.useMemo(
    () => (stats?.daily ?? []).map((row) => ({
      date: format(parseISO(row.date), "d MMM", { locale: el }),
      sent: Number(row.sent),
      opened: Number(row.reads),
    })),
    [stats?.daily],
  );

  const statuses = React.useMemo(
    () => stats
      ? (Object.keys(statusMeta) as Array<keyof typeof statusMeta>)
          .map((key) => ({ key, value: Number(stats.statusBreakdown[key] || 0), ...statusMeta[key] }))
          .filter((item) => item.value > 0)
      : [],
    [stats],
  );

  const partnerRows = React.useMemo(
    () => (stats?.byPartner ?? [])
      .map((partner) => ({
        ...partner,
        openRate: partner.sent > 0 ? Math.round((partner.reads / partner.sent) * 100) : 0,
      }))
      .sort((a, b) => b.sent - a.sent),
    [stats?.byPartner],
  );

  const deliveryRate = stats && stats.totalSent > 0
    ? Math.round((stats.delivered / stats.totalSent) * 100)
    : 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Στατιστικά SMS"
        description="Παράδοση από τον πάροχο, ανοίγματα του lead link και απόδοση ανά συνεργάτη χωρίς ανάμειξη διαφορετικών event types."
        icon={<Smartphone />}
        iconGradient="violet"
        eyebrow="Αναφορές επικοινωνίας"
        actions={
          <>
            <Button variant="outline" className="min-h-11 flex-1 sm:flex-none" asChild>
              <Link to="/admin/messages">Αρχείο μηνυμάτων<ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button variant="outline" className="min-h-11 flex-1 sm:flex-none" onClick={() => query.refetch()} disabled={query.isFetching}>
              <RefreshCw className={cn("mr-2 h-4 w-4", query.isFetching && "animate-spin")} />Ανανέωση
            </Button>
          </>
        }
      />

      <AdminReportNav />

      <div className="admin-filter-bar">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">Χρονικό παράθυρο</p>
          <p className="text-xs text-slate-500">Η σύγκριση τάσης χρησιμοποιεί το αμέσως προηγούμενο ίσο διάστημα.</p>
        </div>
        <div className="flex rounded-xl bg-slate-100 p-1">
          {([7, 14, 30] as Days[]).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setDays(value)}
              className={cn(
                "min-h-10 rounded-lg px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500",
                days === value ? "bg-white text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-900",
              )}
            >
              {value} ημέρες
            </button>
          ))}
        </div>
      </div>

      {query.isLoading ? (
        <AdminMetricSkeletons />
      ) : query.isError || !stats ? (
        <AdminErrorState message={query.error instanceof Error ? query.error.message : "Άγνωστο σφάλμα."} onRetry={() => query.refetch()} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard
              title="SMS που δημιουργήθηκαν"
              value={formatNumber(stats.totalSent)}
              icon={<Send />}
              variant="info"
              description="Στην επιλεγμένη περίοδο"
              trend={stats.trend !== 0 ? { value: Math.abs(stats.trend), direction: stats.trend >= 0 ? "up" : "down" } : undefined}
            />
            <StatCard title="Παραδόθηκαν" value={formatNumber(stats.delivered)} icon={<CheckCircle2 />} variant="success" description={`${deliveryRate}% του συνόλου`} />
            <StatCard title="Άνοιξαν το lead" value={formatNumber(stats.reads)} icon={<Eye />} variant="gradient" description={`${stats.viewRate}% μοναδικά lead clicks`} />
            <StatCard title="Αποτυχίες" value={formatNumber(stats.failed)} icon={<AlertTriangle />} variant={stats.failed > 0 ? "danger" : "default"} description={`${stats.pending} σε αναμονή`} />
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1.55fr)_minmax(320px,0.75fr)]">
            <Card className="admin-panel">
              <CardHeader className="pb-2">
                <CardTitle className="admin-section-title">Αποστολές και ανοίγματα lead</CardTitle>
                <CardDescription>Τα «ανοίγματα» είναι clicks στο ασφαλές lead link, όχι read receipts του SMS.</CardDescription>
              </CardHeader>
              <CardContent className="h-[310px] pl-1 pr-3 sm:h-[360px] sm:pl-3">
                {daily.every((row) => row.sent === 0 && row.opened === 0) ? (
                  <AdminEmptyState title="Δεν υπάρχουν SMS" description="Δεν καταγράφηκαν αποστολές στην επιλεγμένη περίοδο." illustration="stage-reminders" />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={daily} margin={{ top: 16, right: 8, left: -16, bottom: 0 }}>
                      <defs>
                        <linearGradient id="smsSent" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0369a1" stopOpacity={0.18} />
                          <stop offset="95%" stopColor="#0369a1" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" minTickGap={20} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 8px 24px rgba(15,23,42,.08)" }} />
                      <Area type="monotone" dataKey="sent" name="SMS" stroke="#0369a1" strokeWidth={2} fill="url(#smsSent)" />
                      <Area type="monotone" dataKey="opened" name="Lead clicks" stroke="#7c3aed" strokeWidth={2} fillOpacity={0} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card className="admin-panel">
              <CardHeader className="pb-2">
                <CardTitle className="admin-section-title">Κατάσταση παρόχου</CardTitle>
                <CardDescription>Αμοιβαία αποκλειόμενες τελικές/τρέχουσες καταστάσεις.</CardDescription>
              </CardHeader>
              <CardContent>
                {statuses.length === 0 ? (
                  <AdminEmptyState title="Χωρίς status δεδομένα" description="Δεν υπάρχει provider status στην περίοδο." illustration="lead-insights" />
                ) : (
                  <>
                    <div className="relative mx-auto h-52 max-w-xs">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={statuses} dataKey="value" nameKey="label" innerRadius={58} outerRadius={83} paddingAngle={2}>
                            {statuses.map((item) => <Cell key={item.key} fill={item.color} />)}
                          </Pie>
                          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-bold tabular-nums text-slate-950">{stats.totalSent}</span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">σύνολο</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {statuses.map((item) => (
                        <div key={item.key} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                          <span className="flex items-center gap-2 text-sm text-slate-600"><span className={cn("h-2.5 w-2.5 rounded-full", item.bg)} />{item.label}</span>
                          <span className={cn("font-semibold tabular-nums", item.text)}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(300px,0.75fr)]">
            <Card className="admin-panel overflow-hidden">
              <CardHeader className="pb-3">
                <CardTitle className="admin-section-title">Απόδοση ανά συνεργάτη</CardTitle>
                <CardDescription>Ποιοι λαμβάνουν SMS και ποιοι ανοίγουν το lead link.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {partnerRows.length === 0 ? (
                  <AdminEmptyState title="Δεν υπάρχουν δεδομένα συνεργατών" description="Δεν βρέθηκαν partner SMS στην περίοδο." illustration="partner-network" />
                ) : (
                  <div className="divide-y divide-slate-100">
                    {partnerRows.slice(0, 20).map((partner, index) => (
                      <div key={partner.partnerId || `${partner.partnerName}-${index}`} className="flex items-center gap-3 px-4 py-3 sm:px-5">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500"><Users className="h-4 w-4" /></div>
                        <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-slate-900">{partner.partnerName}</p><p className="text-xs text-slate-500">{partner.sent} SMS · {partner.reads} lead clicks</p></div>
                        <div className="w-24 shrink-0 sm:w-36">
                          <div className="mb-1 flex justify-end text-xs font-semibold tabular-nums text-violet-700">{partner.openRate}%</div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-violet-600" style={{ width: `${Math.min(100, partner.openRate)}%` }} /></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="admin-panel">
              <CardHeader className="pb-3"><CardTitle className="admin-section-title">Τι χρειάζεται προσοχή</CardTitle><CardDescription>Signals για operational έλεγχο.</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <div className={cn("rounded-xl border p-3", stats.failed > 0 ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50")}>
                  <div className="flex items-center justify-between gap-3"><span className={cn("text-sm font-semibold", stats.failed > 0 ? "text-rose-800" : "text-emerald-800")}>{stats.failed > 0 ? "Αποτυχίες αποστολής" : "Καμία αποτυχία"}</span><Badge variant="outline" className={stats.failed > 0 ? "border-rose-200 bg-white text-rose-700" : "border-emerald-200 bg-white text-emerald-700"}>{stats.failed}</Badge></div>
                  <p className={cn("mt-1 text-xs leading-5", stats.failed > 0 ? "text-rose-700" : "text-emerald-700")}>{stats.failed > 0 ? "Δείτε τα failures στο Operations Inbox και στο αρχείο μηνυμάτων." : "Ο πάροχος δεν επέστρεψε failures στην περίοδο."}</p>
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-center justify-between gap-3"><span className="text-sm font-semibold text-amber-800">Σε αναμονή provider</span><Badge variant="outline" className="border-amber-200 bg-white text-amber-700">{stats.pending}</Badge></div>
                </div>
                <div className="flex items-start gap-2.5 rounded-xl bg-slate-50 p-3 text-xs leading-5 text-slate-600">
                  <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  Μέσος χρόνος από αποστολή έως lead click: <strong className="text-slate-800">{stats.avgReadTimeHours} ώρες</strong>
                </div>
                {(stats.failed > 0 || stats.pending > 0) && <Button asChild variant="outline" className="min-h-11 w-full"><Link to="/admin/operations">Άνοιγμα Operations Inbox<ArrowRight className="ml-2 h-4 w-4" /></Link></Button>}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default MessageStatistics;
